/**
 * Materialize a design kit's tokens into a real, stack-appropriate theme file.
 *
 * This is what turns Design Studio from "the model SHOULD use these colors" into
 * "these colors ARE the source of truth in the codebase": the generated file is
 * committed and imported, so the kit's palette is enforced by the build, not by
 * a prompt. CSS keeps OKLCH verbatim (native); native stacks get sRGB hex/float
 * via `oklchToHex`.
 *
 * Pure: no disk access — returns `{ path, content }`. Callers (the `design`
 * tool, `/design materialize`, the WebUI button) decide how/where to write.
 */

import type { DesignKitTokens, DesignStack } from '../types/design-kit.js';
import { colorToHex, isColorToken } from './design-color.js';

export interface MaterializeResult {
  /** Suggested project-relative path. */
  path: string;
  /** File contents. */
  content: string;
  /** Human label for the format. */
  format: string;
}

/** Conventional default output path per stack. */
const DEFAULT_PATHS: Record<DesignStack, string> = {
  web: 'src/styles/design-tokens.css',
  'react-native': 'src/theme/design-tokens.ts',
  flutter: 'lib/theme/design_tokens.dart',
  swiftui: 'Theme/DesignTokens.swift',
  compose: 'ui/theme/DesignTokens.kt',
};

function splitColors(set: Record<string, string>): {
  colors: [string, string][];
  others: [string, string][];
} {
  const colors: [string, string][] = [];
  const others: [string, string][] = [];
  for (const [k, v] of Object.entries(set)) {
    (isColorToken(v) ? colors : others).push([k, v]);
  }
  return { colors, others };
}

/** camelCase / kebab → kebab-case for CSS var names. */
function kebab(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').replace(/_/g, '-').toLowerCase();
}

/** PascalCase for Swift/Kotlin identifiers. */
function pascal(name: string): string {
  return name.replace(/(^|[-_])([a-z0-9])/g, (_, __, c: string) => c.toUpperCase());
}

/** hex `#rrggbb[aa]` → { r,g,b,a } as 0..1 floats. */
function hexToRgbFloat(hex: string): { r: number; g: number; b: number; a: number } {
  const h = hex.replace('#', '');
  const r = Number.parseInt(h.slice(0, 2), 16) / 255;
  const g = Number.parseInt(h.slice(2, 4), 16) / 255;
  const b = Number.parseInt(h.slice(4, 6), 16) / 255;
  const a = h.length >= 8 ? Number.parseInt(h.slice(6, 8), 16) / 255 : 1;
  return { r, g, b, a };
}

/** hex `#rrggbb[aa]` → Android/Compose `0xAARRGGBB` literal. */
function hexToArgb(hex: string): string {
  const h = hex.replace('#', '');
  const aa = h.length >= 8 ? h.slice(6, 8) : 'ff';
  return `0x${(aa + h.slice(0, 6)).toUpperCase()}`;
}

function genWebCss(tokens: DesignKitTokens, kitId: string): string {
  const light = tokens.light ?? {};
  const dark = tokens.dark ?? {};
  const vars = (set: Record<string, string>) =>
    Object.entries(set)
      .map(([k, v]) => `  --${kebab(k)}: ${v};`)
      .join('\n');
  // Map color tokens into Tailwind v4 `@theme inline` so `bg-bg`, `text-fg`,
  // `border-border`, etc. resolve to the CSS vars (which flip in `.dark`).
  const colorKeys = Object.keys(light).filter((k) => isColorToken(light[k] ?? ''));
  const themeMap = colorKeys.map((k) => `  --color-${kebab(k)}: var(--${kebab(k)});`).join('\n');
  return `/* Design tokens — kit "${kitId}". Generated by WrongStack Design Studio.
   Edit the kit or run \`design materialize\` again to regenerate. */

:root {
${vars(light)}
}

.dark {
${vars(dark)}
}

@theme inline {
${themeMap}
}
`;
}

function genTsTheme(tokens: DesignKitTokens, kitId: string): string {
  const conv = (set: Record<string, string>) => {
    const lines = Object.entries(set).map(([k, v]) => {
      const hex = colorToHex(v);
      return `    ${JSON.stringify(k)}: ${JSON.stringify(hex ?? v)},`;
    });
    return lines.join('\n');
  };
  return `// Design tokens — kit "${kitId}". Generated by WrongStack Design Studio.
// Colors are converted to sRGB hex (React Native does not parse oklch()).

export const lightTheme = {
${conv(tokens.light ?? {})}
} as const;

export const darkTheme = {
${conv(tokens.dark ?? {})}
} as const;

export type ThemeTokens = typeof lightTheme;
`;
}

function genDart(tokens: DesignKitTokens, kitId: string): string {
  const cls = (name: string, set: Record<string, string>) => {
    const { colors } = splitColors(set);
    const fields = colors
      .map(([k, v]) => {
        const hex = colorToHex(v);
        return hex ? `  static const Color ${k} = Color(${hexToArgb(hex)});` : '';
      })
      .filter(Boolean)
      .join('\n');
    return `class ${name} {\n${fields}\n}`;
  };
  return `// Design tokens — kit "${kitId}". Generated by WrongStack Design Studio.
import 'package:flutter/material.dart';

${cls('AppColorsLight', tokens.light ?? {})}

${cls('AppColorsDark', tokens.dark ?? {})}
`;
}

function genSwift(tokens: DesignKitTokens, kitId: string): string {
  const ext = (name: string, set: Record<string, string>) => {
    const { colors } = splitColors(set);
    const fields = colors
      .map(([k, v]) => {
        const hex = colorToHex(v);
        if (!hex) return '';
        const { r, g, b, a } = hexToRgbFloat(hex);
        return `    static let ${k} = Color(red: ${r.toFixed(4)}, green: ${g.toFixed(4)}, blue: ${b.toFixed(4)}, opacity: ${a.toFixed(4)})`;
      })
      .filter(Boolean)
      .join('\n');
    return `enum ${name} {\n${fields}\n}`;
  };
  return `// Design tokens — kit "${kitId}". Generated by WrongStack Design Studio.
import SwiftUI

${ext('AppColorsLight', tokens.light ?? {})}

${ext('AppColorsDark', tokens.dark ?? {})}
`;
}

function genKotlin(tokens: DesignKitTokens, kitId: string): string {
  const obj = (name: string, set: Record<string, string>) => {
    const { colors } = splitColors(set);
    const fields = colors
      .map(([k, v]) => {
        const hex = colorToHex(v);
        return hex ? `    val ${pascal(k)} = Color(${hexToArgb(hex)})` : '';
      })
      .filter(Boolean)
      .join('\n');
    return `object ${name} {\n${fields}\n}`;
  };
  return `// Design tokens — kit "${kitId}". Generated by WrongStack Design Studio.
import androidx.compose.ui.graphics.Color

${obj('AppColorsLight', tokens.light ?? {})}

${obj('AppColorsDark', tokens.dark ?? {})}
`;
}

/**
 * Generate a stack-appropriate theme file from a kit's (override-applied) tokens.
 * `outPath` overrides the conventional default path.
 */
export function materializeTokens(opts: {
  tokens: DesignKitTokens;
  stack: DesignStack;
  kitId: string;
  outPath?: string | undefined;
}): MaterializeResult {
  const { tokens, stack, kitId } = opts;
  let content: string;
  let format: string;
  switch (stack) {
    case 'react-native':
      content = genTsTheme(tokens, kitId);
      format = 'TypeScript theme (hex)';
      break;
    case 'flutter':
      content = genDart(tokens, kitId);
      format = 'Dart ColorScheme constants';
      break;
    case 'swiftui':
      content = genSwift(tokens, kitId);
      format = 'SwiftUI Color constants';
      break;
    case 'compose':
      content = genKotlin(tokens, kitId);
      format = 'Compose Color constants';
      break;
    default:
      content = genWebCss(tokens, kitId);
      format = 'CSS variables + Tailwind v4 @theme (OKLCH)';
      break;
  }
  return { path: opts.outPath?.trim() || DEFAULT_PATHS[stack], content, format };
}
